import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faAngleLeft,
  faAngleRight,
  faBuilding,
  faCalendarAlt,
  faChartBar,
  faCoins,
  faEye,
  faFileCsv,
  faFilter,
  faHospital,
  faSearch,
  faSort,
  faSortDown,
  faSortUp,
  faSpinner,
  faSyncAlt,
  faTags,
  faTimes,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-hot-toast";
import { BASE_URL } from "../../config";

const toDateInput = (date) => {
  const timezoneOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - timezoneOffset).toISOString().slice(0, 10);
};

const getDefaultFilters = () => {
  const today = new Date();
  return {
    trans_date_from: `${today.getFullYear()}-01-01`,
    trans_date_to: toDateInput(today),
    date_received_from: "",
    date_received_to: "",
    date_rtp_from: "",
    date_rtp_to: "",
    search: "",
    transaction_number: "",
    loa_number: "",
    provider_name: "",
    member_name: "",
    corp_name: "",
    coverage_type: "",
    rtp_reason: "",
  };
};

const tableColumns = [
  { key: "transaction_number", label: "Transaction Number", sortable: true, className: "min-w-[170px]" },
  { key: "loa_number", label: "LOA NUMBER", sortable: true, className: "min-w-[150px]" },
  { key: "transactions_date", label: "Transactions Date", sortable: true, type: "date", className: "min-w-[170px]" },
  { key: "date_received", label: "Date Received", sortable: true, type: "date", className: "min-w-[160px]" },
  { key: "provider_name", label: "Provider Name", sortable: true, className: "min-w-[230px] max-w-[300px]" },
  { key: "member_full_name", label: "Member Full Name", sortable: true, className: "min-w-[220px] max-w-[280px]" },
  { key: "corp_name", label: "Corp Name", sortable: true, className: "min-w-[220px] max-w-[280px]" },
  { key: "coverage_type", label: "Coverage Type", sortable: true, className: "min-w-[150px]" },
  { key: "rtp_reason", label: "RTP Reason", sortable: true, className: "min-w-[260px] max-w-[360px]" },
  { key: "loa_amount", label: "LOA Amount", sortable: true, type: "amount", className: "min-w-[150px] text-right" },
  { key: "date_rtp", label: "Date RTP", sortable: true, type: "date", className: "min-w-[160px]" },
  { key: "actions", label: "Action", className: "min-w-[110px] text-center" },
];

const detailFields = tableColumns.filter((column) => column.key !== "actions");

const perPageOptions = [10, 20, 50, 100, 200];

const resolveErrorMessage = (error, fallback) => {
  const message = error?.response?.data?.message;
  if (!message) return fallback;
  if (typeof message === "string") return message;
  if (Array.isArray(message)) return message.join(", ");
  if (typeof message === "object") return Object.values(message).flat().join(", ");
  return fallback;
};

const formatNumber = (value) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Number(value || 0));

const formatAmount = (value) =>
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ClaimsRtpReportQueryRequest = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total_records: 0,
    total_pages: 1,
  });
  const [filters, setFilters] = useState(getDefaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(getDefaultFilters);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [sortBy, setSortBy] = useState("date_rtp");
  const [sortDirection, setSortDirection] = useState("desc");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const token = localStorage.getItem("authentication/token");

  const requestParams = useCallback(
    (sourceFilters, includePagination = true) => {
      const params = {
        ...sourceFilters,
        sort_by: sortBy,
        sort_direction: sortDirection,
      };

      if (includePagination) {
        params.page = page;
        params.per_page = perPage;
      }

      Object.keys(params).forEach((key) => {
        if (params[key] === "") {
          delete params[key];
        }
      });

      return params;
    },
    [page, perPage, sortBy, sortDirection]
  );

  const validateTransactionRange = useCallback((sourceFilters) => {
    if (!sourceFilters.trans_date_from || !sourceFilters.trans_date_to) {
      toast.error("Transaction Date From and To are required.");
      return false;
    }

    if (sourceFilters.trans_date_from > sourceFilters.trans_date_to) {
      toast.error("Transaction Date To must be on or after Transaction Date From.");
      return false;
    }

    return true;
  }, []);

  const fetchRows = useCallback(async () => {
    if (!validateTransactionRange(appliedFilters)) return;

    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/claims/processing/rtp-report-query-request`, {
        params: requestParams(appliedFilters, true),
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setRows(response.data?.data || []);
      setSummary(response.data?.summary || {});
      setPagination(
        response.data?.pagination || {
          current_page: 1,
          per_page: perPage,
          total_records: 0,
          total_pages: 1,
        }
      );
    } catch (error) {
      toast.error(resolveErrorMessage(error, "Failed to fetch FOR RTP report rows."));
      setRows([]);
      setSummary({});
      setPagination({
        current_page: 1,
        per_page: perPage,
        total_records: 0,
        total_pages: 1,
      });
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, perPage, requestParams, token, validateTransactionRange]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const metricCards = useMemo(
    () => [
      {
        label: "Total Records",
        value: formatNumber(summary.total_records),
        icon: faChartBar,
        accent: "from-sky-500 to-blue-600",
        surface: "from-white to-sky-50",
      },
      {
        label: "Total LOA Amount",
        value: `PHP ${formatAmount(summary.total_loa_amount)}`,
        icon: faCoins,
        accent: "from-amber-500 to-orange-500",
        surface: "from-white to-amber-50",
      },
      {
        label: "Providers",
        value: formatNumber(summary.total_providers),
        icon: faHospital,
        accent: "from-emerald-500 to-teal-600",
        surface: "from-white to-emerald-50",
      },
      {
        label: "Corporates",
        value: formatNumber(summary.total_corporates),
        icon: faBuilding,
        accent: "from-rose-500 to-red-500",
        surface: "from-white to-rose-50",
      },
      {
        label: "Members",
        value: formatNumber(summary.total_members),
        icon: faUsers,
        accent: "from-indigo-500 to-blue-500",
        surface: "from-white to-indigo-50",
      },
      {
        label: "RTP Reasons",
        value: formatNumber(summary.total_rtp_reasons),
        icon: faTags,
        accent: "from-cyan-500 to-emerald-500",
        surface: "from-white to-cyan-50",
      },
    ],
    [summary]
  );

  const showingText = useMemo(() => {
    const total = Number(pagination.total_records || 0);
    if (!total) return "No records";
    const from = (page - 1) * perPage + 1;
    const to = Math.min(page * perPage, total);
    return `Showing ${from} to ${to} of ${formatNumber(total)} records`;
  }, [page, perPage, pagination.total_records]);

  const handleFilterChange = (key, value) => {
    setFilters((previous) => ({
      ...previous,
      [key]: value,
    }));
  };

  const applyFilters = () => {
    if (!validateTransactionRange(filters)) return;
    setPage(1);
    setAppliedFilters(filters);
  };

  const resetFilters = () => {
    const defaults = getDefaultFilters();
    setFilters(defaults);
    setAppliedFilters(defaults);
    setPage(1);
    setSortBy("date_rtp");
    setSortDirection("desc");
  };

  const handleSort = (columnKey) => {
    if (sortBy === columnKey) {
      setSortDirection((previous) => (previous === "asc" ? "desc" : "asc"));
      setPage(1);
      return;
    }

    setSortBy(columnKey);
    setSortDirection("asc");
    setPage(1);
  };

  const getSortIcon = (columnKey) => {
    if (sortBy !== columnKey) return faSort;
    return sortDirection === "asc" ? faSortUp : faSortDown;
  };

  const handleExportCsv = async () => {
    if (!validateTransactionRange(appliedFilters)) return;

    try {
      setExporting(true);
      const response = await axios.get(`${BASE_URL}/claims/processing/rtp-report-query-request/export`, {
        params: requestParams(appliedFilters, false),
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: "blob",
      });

      const disposition = response.headers?.["content-disposition"] || "";
      const matchedFileName = disposition.match(/filename="?([^"]+)"?/i);
      const fileName = matchedFileName?.[1] || `for_rtp_report_query_request_${Date.now()}.csv`;
      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(resolveErrorMessage(error, "Failed to export CSV."));
    } finally {
      setExporting(false);
    }
  };

  const renderCell = (row, column) => {
    if (column.key === "actions") {
      return (
        <button
          type="button"
          onClick={() => setSelectedRow(row)}
          title="View details"
          className="inline-flex items-center justify-center gap-2 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-emerald-700"
        >
          <FontAwesomeIcon icon={faEye} />
          View
        </button>
      );
    }

    const value = row?.[column.key];
    if (column.type === "amount") return formatAmount(value);
    if (column.type === "date") return formatDateTime(value);
    if (value === null || value === undefined || value === "") return "-";
    return value;
  };

  const totalPages = Math.max(1, Number(pagination.total_pages || 1));

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6">
      <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase text-emerald-700">
            <FontAwesomeIcon icon={faCalendarAlt} />
            <span>Claims</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-950">
            FOR RTP REPORT QUERY REQUEST
          </h1>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={fetchRows}
            disabled={loading}
            title="Refresh"
            className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FontAwesomeIcon icon={loading ? faSpinner : faSyncAlt} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={exporting}
            title="Export CSV"
            className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FontAwesomeIcon icon={exporting ? faSpinner : faFileCsv} className={exporting ? "animate-spin" : ""} />
            Export CSV
          </button>
        </div>
      </div>

      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-6">
        {metricCards.map((card) => (
          <div
            key={card.label}
            className={`rounded-lg border border-slate-200 bg-gradient-to-br ${card.surface} p-4 shadow-sm`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase text-slate-500">{card.label}</p>
                <p className="mt-2 break-words text-xl font-bold text-slate-950">{card.value}</p>
              </div>
              <div
                className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-gradient-to-br ${card.accent} text-white shadow-sm`}
              >
                <FontAwesomeIcon icon={card.icon} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mb-5 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className={`${isFiltersOpen ? "mb-4" : ""} flex items-center justify-between gap-3`}>
          <div className="flex items-center gap-2 text-sm font-bold text-slate-900">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-slate-900 text-white">
              <FontAwesomeIcon icon={faFilter} />
            </span>
            Filters
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {isFiltersOpen && (
              <button
                type="button"
                onClick={resetFilters}
                title="Reset filters"
                className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-100"
              >
                <FontAwesomeIcon icon={faTimes} />
                Reset
              </button>
            )}
            <button
              type="button"
              onClick={() => setIsFiltersOpen((previous) => !previous)}
              title={isFiltersOpen ? "Hide filters" : "Show filters"}
              className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-2 text-xs font-semibold text-white transition hover:bg-slate-800"
            >
              <FontAwesomeIcon icon={faFilter} />
              {isFiltersOpen ? "Hide Filters" : "Show Filters"}
            </button>
          </div>
        </div>

        {isFiltersOpen && (
          <>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Transaction Date From</label>
                <input
                  type="date"
                  value={filters.trans_date_from}
                  onChange={(event) => handleFilterChange("trans_date_from", event.target.value)}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Transaction Date To</label>
                <input
                  type="date"
                  value={filters.trans_date_to}
                  onChange={(event) => handleFilterChange("trans_date_to", event.target.value)}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </div>
              <div className="xl:col-span-2">
                <label className="mb-1 block text-xs font-semibold text-slate-600">Search</label>
                <div className="relative">
                  <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    value={filters.search}
                    onChange={(event) => handleFilterChange("search", event.target.value)}
                    className="w-full rounded-md border border-slate-200 py-2 pl-9 pr-3 text-sm text-slate-800 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                    placeholder="Transaction, LOA, provider, member, corp, coverage, reason"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Transaction Number</label>
                <input
                  value={filters.transaction_number}
                  onChange={(event) => handleFilterChange("transaction_number", event.target.value)}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">LOA NUMBER</label>
                <input
                  value={filters.loa_number}
                  onChange={(event) => handleFilterChange("loa_number", event.target.value)}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Provider Name</label>
                <input
                  value={filters.provider_name}
                  onChange={(event) => handleFilterChange("provider_name", event.target.value)}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Member Name</label>
                <input
                  value={filters.member_name}
                  onChange={(event) => handleFilterChange("member_name", event.target.value)}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Corp Name</label>
                <input
                  value={filters.corp_name}
                  onChange={(event) => handleFilterChange("corp_name", event.target.value)}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Coverage Type</label>
                <input
                  value={filters.coverage_type}
                  onChange={(event) => handleFilterChange("coverage_type", event.target.value)}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </div>
              <div className="xl:col-span-2">
                <label className="mb-1 block text-xs font-semibold text-slate-600">RTP Reason</label>
                <input
                  value={filters.rtp_reason}
                  onChange={(event) => handleFilterChange("rtp_reason", event.target.value)}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Date Received From</label>
                <input
                  type="date"
                  value={filters.date_received_from}
                  onChange={(event) => handleFilterChange("date_received_from", event.target.value)}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Date Received To</label>
                <input
                  type="date"
                  value={filters.date_received_to}
                  onChange={(event) => handleFilterChange("date_received_to", event.target.value)}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Date RTP From</label>
                <input
                  type="date"
                  value={filters.date_rtp_from}
                  onChange={(event) => handleFilterChange("date_rtp_from", event.target.value)}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Date RTP To</label>
                <input
                  type="date"
                  value={filters.date_rtp_to}
                  onChange={(event) => handleFilterChange("date_rtp_to", event.target.value)}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={applyFilters}
                disabled={loading}
                title="Apply filters"
                className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FontAwesomeIcon icon={loading ? faSpinner : faFilter} className={loading ? "animate-spin" : ""} />
                Apply Filters
              </button>
            </div>
          </>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
          <p className="text-sm font-semibold text-slate-700">{showingText}</p>
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold uppercase text-slate-500">Display</label>
            <select
              value={perPage}
              onChange={(event) => {
                setPerPage(Number(event.target.value));
                setPage(1);
              }}
              className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
            >
              {perPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse text-left text-sm">
            <thead className="bg-slate-100 text-xs uppercase text-slate-600">
              <tr>
                {tableColumns.map((column) => (
                  <th key={column.key} className={`px-4 py-3 font-bold ${column.className || ""}`}>
                    {column.sortable ? (
                      <button
                        type="button"
                        onClick={() => handleSort(column.key)}
                        title={`Sort ${column.label}`}
                        className={`flex w-full items-center gap-2 ${column.type === "amount" ? "justify-end" : "justify-start"}`}
                      >
                        <span>{column.label}</span>
                        <FontAwesomeIcon icon={getSortIcon(column.key)} className="text-slate-400" />
                      </button>
                    ) : (
                      column.label
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                Array.from({ length: Math.min(perPage, 10) }).map((_, rowIndex) => (
                  <tr key={`loading-${rowIndex}`} className="animate-pulse">
                    {tableColumns.map((column) => (
                      <td key={column.key} className="px-4 py-3">
                        <div className="h-4 rounded bg-slate-100" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : rows.length > 0 ? (
                rows.map((row, rowIndex) => (
                  <tr key={`${row.transaction_number || "row"}-${rowIndex}`} className="hover:bg-slate-50">
                    {tableColumns.map((column) => (
                      <td
                        key={column.key}
                        className={`px-4 py-3 align-top text-slate-700 ${
                          column.type === "amount" ? "text-right font-semibold tabular-nums" : ""
                        } ${column.key === "actions" ? "text-center" : ""} ${
                          ["provider_name", "member_full_name", "corp_name", "rtp_reason"].includes(column.key)
                            ? "whitespace-normal break-words"
                            : "whitespace-nowrap"
                        }`}
                      >
                        {renderCell(row, column)}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={tableColumns.length} className="px-4 py-12 text-center text-sm text-slate-500">
                    No records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-slate-200 px-4 py-3 md:flex-row md:items-center md:justify-between">
          <p className="text-xs font-medium text-slate-500">
            Page {page} of {totalPages}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={page <= 1 || loading}
              title="First page"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <FontAwesomeIcon icon={faAngleDoubleLeft} />
            </button>
            <button
              type="button"
              onClick={() => setPage((previous) => Math.max(1, previous - 1))}
              disabled={page <= 1 || loading}
              title="Previous page"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <FontAwesomeIcon icon={faAngleLeft} />
            </button>
            <button
              type="button"
              onClick={() => setPage((previous) => Math.min(totalPages, previous + 1))}
              disabled={page >= totalPages || loading}
              title="Next page"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <FontAwesomeIcon icon={faAngleRight} />
            </button>
            <button
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={page >= totalPages || loading}
              title="Last page"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <FontAwesomeIcon icon={faAngleDoubleRight} />
            </button>
          </div>
        </div>
      </div>

      {selectedRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4">
          <div className="max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-lg bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase text-emerald-700">RTP Report Details</p>
                <h2 className="mt-1 break-words text-xl font-bold text-slate-950">
                  {selectedRow.transaction_number || "-"}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setSelectedRow(null)}
                title="Close"
                className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-100"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="max-h-[calc(90vh-82px)] overflow-y-auto p-5">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {detailFields.map((field) => (
                  <div key={field.key} className="rounded-md border border-slate-200 bg-slate-50 p-3">
                    <p className="text-xs font-semibold uppercase text-slate-500">{field.label}</p>
                    <p
                      className={`mt-1 text-sm font-semibold text-slate-900 ${
                        ["provider_name", "member_full_name", "corp_name", "rtp_reason"].includes(field.key)
                          ? "whitespace-pre-wrap break-words"
                          : "break-words"
                      }`}
                    >
                      {renderCell(selectedRow, field)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClaimsRtpReportQueryRequest;
